import { getToken } from '@bettercomp/ams-spa-auth';
import { getAmsSession } from 'service/amsSession';

const BEARER_PREFIX = 'Bearer ';

/**
 * Renew-then-retry decision for a 401 that was sent with an AMS bearer.
 * Returns 'retry' when the caller should re-send the request once with the
 * current token: either a renewal already landed while the request was in
 * flight (the sent token is stale), or an unauthorized-triggered renewal just
 * minted a fresh token. 'suppressed' and 'resolved' renewals surface the 401 —
 * terminal outcomes reach the auth gate through the session's own publish, so
 * no logout is initiated from the fetch layer.
 */
export const resolveUnauthorized = async (sentToken: string): Promise<'retry' | 'surface'> => {
  if (getToken() !== sentToken) {
    return 'retry';
  }
  const session = getAmsSession();
  if (!session) {
    return 'surface';
  }
  const result = await session.renew({ trigger: 'unauthorized' });
  return result.status === 'renewed' ? 'retry' : 'surface';
};

/**
 * fetch wrapper shared by every client stack: a transparent passthrough while
 * no AMS session is live (legacy 401 semantics stay untouched), and a
 * single-retry renew-on-401 when the request carried the AMS bearer.
 * Renewal storms coalesce on RenewalPolicy's shared in-flight promise.
 */
export const fetchWithRenewal: typeof fetch = async (input, init) => {
  if (!getToken()) {
    return fetch(input, init);
  }

  // Bodies are one-shot streams: dispatch a clone so the original stays
  // readable for the single retry.
  const request = new Request(input, init);
  const sentAuth = request.headers.get('Authorization');
  const response = await fetch(request.clone());

  if (response.status !== 401 || !sentAuth?.startsWith(BEARER_PREFIX)) {
    return response;
  }

  const action = await resolveUnauthorized(sentAuth.slice(BEARER_PREFIX.length));
  const freshToken = getToken();
  if (action === 'surface' || !freshToken) {
    return response;
  }

  request.headers.set('Authorization', `${BEARER_PREFIX}${freshToken}`);
  return fetch(request);
};
